import { useState, useCallback, useEffect } from "react";

interface UseCollapsibleSidebarOptions {
  defaultOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  enableKeyboard?: boolean;
  responsive?: boolean;
}

export function useCollapsibleSidebar(
  options: UseCollapsibleSidebarOptions = {},
) {
  const {
    defaultOpen = false,
    onOpen,
    onClose,
    enableKeyboard = true,
    responsive = true,
  } = options;

  const [isOpen, setIsOpen] = useState(
    defaultOpen && (typeof window === "undefined" || window.innerWidth >= 768),
  );
  const [sidebarWidth, setSidebarWidth] = useState(320); // Default width in pixels
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  // Handle responsive sidebar width
  useEffect(() => {
    if (!responsive) return;

    const handleResize = () => {
      const screenWidth = window.innerWidth;
      const wasMobile = screenWidth < 768;
      setIsMobile(wasMobile);
      if (wasMobile) {
        setSidebarWidth(screenWidth); // Full width on mobile
      } else if (screenWidth < 1024) {
        setSidebarWidth(320); // Smaller on tablet
      } else {
        setSidebarWidth(384); // 384px (w-96) on desktop
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [responsive]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!enableKeyboard) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Close on Escape
      if (e.key === "Escape" && isOpen) {
        close();
      }
      // Toggle with Cmd/Ctrl + B
      if ((e.metaKey || e.ctrlKey) && e.key === "b") {
        e.preventDefault();
        toggle();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [isOpen, enableKeyboard]);

  const open = useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  // Calculate dynamic styles
  const mainContentStyle = {
    marginLeft: isOpen ? `${sidebarWidth}px` : 0,
    marginRight: 0,
    transition: "margin 300ms cubic-bezier(0.4, 0, 0.2, 1)",
  };

  const rightSidebarStyle = {
    marginRight: isOpen ? `${sidebarWidth}px` : 0,
    transition: "margin 300ms cubic-bezier(0.4, 0, 0.2, 1)",
  };

  const sidebarStyle = {
    width: isOpen ? `${sidebarWidth}px` : 0,
    transition: "width 300ms cubic-bezier(0.4, 0, 0.2, 1)",
  };

  return {
    isOpen,
    isMobile,
    open,
    close,
    toggle,
    sidebarWidth,
    mainContentStyle,
    rightSidebarStyle,
    sidebarStyle,
  };
}
